import type { MentionsChecklistResult } from '../../../shared/types';
import SectionBlock from './SectionBlock';

/* Section « Mentions obligatoires (vérification automatique) » — restitue la
 * checklist déterministe calculée par règles côté serveur (R5122-3) : une
 * ligne par mention (présente / absente / non vérifiable) + les signaux
 * d'alerte détectés par motifs. Même coquille visuelle que les autres
 * sections du rapport (SectionBlock + copie au survol). */

function presenceIcon(present: boolean | null) {
  if (present === true) {
    return (
      <svg className="w-4 h-4 mt-0.5 shrink-0 text-emerald-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" aria-label="Mention détectée">
        <path d="M20 6 9 17l-5-5" />
      </svg>
    );
  }
  if (present === false) {
    return (
      <svg className="w-4 h-4 mt-0.5 shrink-0 text-red-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" aria-label="Mention absente">
        <path d="M18 6 6 18M6 6l12 12" />
      </svg>
    );
  }
  return (
    <svg className="w-4 h-4 mt-0.5 shrink-0 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" aria-label="Non vérifiable sur cette entrée">
      <path d="M5 12h14" />
    </svg>
  );
}

function buildCopyText(checklist: MentionsChecklistResult): string {
  const lines: string[] = ['## Mentions obligatoires (vérification automatique)', ''];
  if (checklist.regime) lines.push(`Régime : ${checklist.regime}`, '');
  for (const item of checklist.items) {
    const mark = item.present === true ? '[x]' : item.present === false ? '[ ]' : '[—]';
    lines.push(`${mark} ${item.label} (${item.legalBasis})`);
    if (item.detail) lines.push(`    ${item.detail}`);
  }
  if (checklist.redFlags.length > 0) {
    lines.push('', '### Signaux d\'alerte détectés', '');
    for (const flag of checklist.redFlags) {
      lines.push(`- ${flag.pattern} — ${flag.explanation} (${flag.legalBasis})`);
      if (flag.excerpt) lines.push(`  Extrait : « ${flag.excerpt} »`);
    }
  }
  return lines.join('\n');
}

export default function MentionsChecklistSection({ checklist }: { checklist: MentionsChecklistResult }) {
  if (checklist.items.length === 0 && checklist.redFlags.length === 0) return null;

  return (
    <SectionBlock
      className="rounded-xl border border-slate-200 bg-cream-card px-6 py-6"
      copyText={buildCopyText(checklist)}
    >
      <h3 className="font-display text-sm text-slate-700 uppercase tracking-wide mb-1">
        Mentions obligatoires (vérification automatique)
      </h3>
      <p className="text-xs text-slate-400 mb-5">
        {checklist.regime ? <>Régime appliqué&nbsp;: {checklist.regime}. </> : null}
        Vérification par règles (indépendante des juges IA)&nbsp;— elle complète le verdict, elle ne le remplace pas.
      </p>

      {checklist.items.length > 0 && (
        <ul className="space-y-3.5">
          {checklist.items.map((item) => (
            <li key={item.key} className="flex items-start gap-2.5 text-sm leading-relaxed">
              {presenceIcon(item.present)}
              <div className="min-w-0">
                <p className="text-slate-900">
                  {item.label}
                  {item.legalBasis && (
                    <span className="ml-2 text-xs text-slate-400">{item.legalBasis}</span>
                  )}
                </p>
                {item.detail && (
                  <p className="mt-0.5 text-xs text-slate-500 leading-relaxed">{item.detail}</p>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {checklist.redFlags.length > 0 && (
        <div className={`${checklist.items.length > 0 ? 'mt-5 pt-5 border-t border-slate-100' : ''}`}>
          <h4 className="font-display text-xs text-red-700 uppercase tracking-wide mb-3">
            Signaux d'alerte détectés
          </h4>
          <ul className="space-y-4">
            {checklist.redFlags.map((flag, i) => (
              <li key={i} className="text-sm leading-relaxed">
                <p className="font-medium text-slate-900 mb-0.5">{flag.pattern}</p>
                {flag.excerpt && (
                  <p className="text-xs italic text-slate-500">«&nbsp;{flag.excerpt}&nbsp;»</p>
                )}
                <p className="text-slate-600 mt-0.5">{flag.explanation}</p>
                <p className="text-xs text-red-600 mt-1">Réf.&nbsp;: {flag.legalBasis}</p>
              </li>
            ))}
          </ul>
        </div>
      )}
    </SectionBlock>
  );
}
